// Source locations come from the YAML parser (which also accepts JSON). Never
// serialize parsed values back into the display: comments and spacing are source.
import { inlineDiff } from './inline-diff.js';
import { load } from 'js-yaml';
import { diffArrays } from 'diff';
import { appendPropertyPath, isIgnoredPath } from './records/path.js';

function sourceNodes(text, keyColumn) {
  const stack = [];
  let root;
  load(text, { listener(event, state) {
    if (event === 'open') {
      const node = { start: state.position, children: [] };
      if (stack.length) stack.at(-1).children.push(node);
      else root = node;
      stack.push(node);
    } else {
      Object.assign(stack.pop(), { end: state.position, kind: state.kind, value: state.result });
    }
  } });
  const fields = new Map();
  const groups = new Map();
  function visit(node, path = '', keyNode = node) {
    if (!node) return;
    while (node.children.length === 1 && node.children[0].value === node.value) node = node.children[0];
    let start = Math.min(node.start, text.length);
    while (start < node.end && /\s/.test(text[start])) start++;
    let end = Math.min(node.end, text.length);
    while (end > start && /\s/.test(text[end - 1])) end--;
    let keyStart = keyNode.start;
    while (keyStart < text.length && /\s/.test(text[keyStart])) keyStart++;
    fields.set(path, { start, end, keyStart, kind: node.kind });
    if (node.kind === 'mapping' && node.children.length % 2 === 0) {
      const paths = [];
      for (let i = 0; i < node.children.length; i += 2) {
        const key = node.children[i];
        if (typeof key.value !== 'string' && typeof key.value !== 'number') continue;
        const childPath = appendPropertyPath(path, key.value);
        paths.push(childPath);
        visit(node.children[i + 1], childPath, key);
      }
      groups.set(path, paths);
    } else if (node.kind === 'sequence') {
      const paths = node.children.map((child, i) => {
        const id = !path && keyColumn && child.value && typeof child.value === 'object' ? child.value[keyColumn] : undefined;
        return `${path}[${id === undefined ? i : `key:${JSON.stringify(String(id))}`}]`;
      });
      groups.set(path, paths);
      node.children.forEach((child, i) => visit(child, paths[i]));
    }
  }
  visit(root);
  return { fields, groups };
}

export function structuredSource(left, right, result, options = {}) {
  const sources = [left, right];
  const maps = sources.map(text => sourceNodes(text, result.keyColumn));
  const lines = sources.map(text => text.split('\n').map((text, i) => ({ text: text.replace(/\r$/, ''), number: i + 1, marks: [], moves: [] })));
  const starts = sources.map(text => { const out = [0]; for (let i = 0; i < text.length; i++) if (text[i] === '\n') out.push(i + 1); return out; });
  const lineAt = (side, offset) => {
    const list = starts[side];
    let lo = 0, hi = list.length;
    while (lo + 1 < hi) { const mid = (lo + hi) >>> 1; if (list[mid] <= offset) lo = mid; else hi = mid; }
    return lo;
  };
  const ignore = new Set(options.ignoreFields || []);
  const peers = new Map();
  for (const [path, node] of maps[0].fields) {
    const other = maps[1].fields.get(path);
    if (node.kind === 'scalar' && other?.kind === 'scalar') { peers.set(node, other); peers.set(other, node); }
  }
  // `addr` is the SEMANTIC address (a leaf path, or a record key) that caused
  // this mark. Stamping it on the line lets the change navigator find a stop in
  // the source view, which otherwise only knows byte offsets.
  function mark(side, node, kind, precise = true, addr = null) {
    if (!node) return;
    const peer = peers.get(node);
    if (precise && peer) {
      const oldNode = side ? peer : node, newNode = side ? node : peer;
      const parts = inlineDiff(sources[0].slice(oldNode.start, oldNode.end), sources[1].slice(newNode.start, newNode.end), 'word');
      let offset = node.start;
      for (const part of parts) {
        if (part.type === (side ? 'old' : 'new')) continue;
        if (part.type !== 'ctx') mark(side, { start: offset, end: offset + part.text.length }, kind, false, addr);
        offset += part.text.length;
      }
      return;
    }
    const first = lineAt(side, node.start), last = lineAt(side, Math.max(node.start, node.end - 1));
    for (let i = first; i <= last; i++) {
      const row = lines[side][i];
      row.kind = kind;
      if (addr != null) {
        if (!row.addrs) row.addrs = [];
        if (!row.addrs.includes(addr)) row.addrs.push(addr);
      }
      row.marks.push({ start: Math.max(0, node.start - starts[side][i]), end: Math.min(row.text.length, node.end - starts[side][i]) });
    }
  }
  // Use the semantic engine's decisions, including ignored fields and types.
  for (const row of result.render.rows || []) {
    if (!row.path || row.status === 'context') continue;
    for (let side = 0; side < 2; side++) {
      if (side === 0 && row.status === 'add' || side === 1 && row.status === 'remove') continue;
      const prefix = maps[side].fields.get('')?.kind === 'sequence' ? '[0]' : '';
      const path = row.path === '$' ? prefix : `${prefix}${prefix && !row.path.startsWith('[') ? '.' : ''}${row.path}`;
      mark(side, maps[side].fields.get(path), side ? 'add' : 'del', true, `path:${row.path}`);
    }
  }
  // Array results expose record changes rather than the single-document ledger.
  for (const c of result.changes || []) {
    if (['add', 'remove', 'change', 'type'].includes(c.kind) && !result.render.rows) {
      const index = /^#(\d+)$/.exec(c.recordKey || c.path || '');
      const prefix = index ? `[${index[1]}]` : '';
      const path = c.path === '$' || /^#\d+$/.test(c.path) ? prefix : `${prefix}${prefix && !c.path.startsWith('[') ? '.' : ''}${c.path}`;
      if (c.kind !== 'add') mark(0, maps[0].fields.get(path), 'del', true, `path:${c.path}`);
      if (c.kind !== 'remove') mark(1, maps[1].fields.get(path), 'add', true, `path:${c.path}`);
    }
    if (c.kind === 'record-add' || c.kind === 'record-remove') {
      const side = c.kind === 'record-add' ? 1 : 0;
      const path = result.keyColumn ? `[key:${JSON.stringify(c.recordKey)}]` : `[${String(c.recordKey).replace(/^#/, '')}]`;
      mark(side, maps[side].fields.get(path), side ? 'add' : 'del', true, `record:${c.recordKey}`);
    }
    if (c.kind === 'column-add' || c.kind === 'column-remove') {
      const side = c.kind === 'column-add' ? 1 : 0;
      for (const [path, node] of maps[side].fields) if (path.endsWith(`.${c.name}`)) mark(side, node, side ? 'add' : 'del');
    }
    if (c.kind === 'record-modify') {
      for (const cell of c.cells) {
        for (let side = 0; side < 2; side++) {
          const prefix = result.keyColumn ? `[key:${JSON.stringify(c.recordKey)}]` : `[${String(c.recordKey).replace(/^#/, '')}]`;
          const path = `${prefix}${cell.name.startsWith('[') ? '' : '.'}${cell.name}`;
          mark(side, maps[side].fields.get(path), side ? 'add' : 'del');
        }
      }
    }
  }
  // The semantic model reports leaf changes. Include the source wrapper of an
  // absent object/array too, so its property name and delimiters aren't left plain.
  for (let side = 0; side < 2; side++) {
    for (const [path, node] of maps[side].fields) {
      if (!path || !['mapping', 'sequence'].includes(node.kind) || maps[1 - side].fields.has(path) || isIgnoredPath(path, ignore)) continue;
      const descendants = [...maps[side].fields].filter(([, child]) => child.start >= node.start && child.end <= node.end);
      if (descendants.some(([childPath]) => isIgnoredPath(childPath, ignore))) continue;
      const first = lineAt(side, node.keyStart);
      const last = lineAt(side, Math.max(node.keyStart, node.end - 1));
      const kind = side ? 'add' : 'del';
      if (!lines[side].slice(first, last + 1).some(row => row.kind === kind)) continue;
      mark(side, { start: node.keyStart, end: node.end }, kind, false);
    }
  }
  // Only relative sibling reordering is a move. Insertions above a field do
  // not turn every subsequent line into a moved line.
  for (const [parent, a] of maps[0].groups) {
    const b = maps[1].groups.get(parent);
    if (!b) continue;
    const aset = new Set(a), bset = new Set(b);
    const l = a.filter(p => bset.has(p)), r = b.filter(p => aset.has(p));
    for (const part of diffArrays(l, r)) if (part.removed) for (const path of part.value) {
      if (isIgnoredPath(path, ignore)) continue;
      const from = lineAt(0, maps[0].fields.get(path).keyStart);
      const to = lineAt(1, maps[1].fields.get(path).keyStart);
      // Each arrow describes the jump from THIS row to the linked row.
      const direction = (current, target) => target < current ? '↑' : target > current ? '↓' : '↔';
      lines[0][from].moves.push({ direction: direction(from, to), line: to + 1, side: 'right', path });
      lines[1][to].moves.push({ direction: direction(to, from), line: from + 1, side: 'left', path });
      // Colour the complete moved field/block, including its nested lines.
      // Keep value-edit marks separate so a moved edit retains red/green.
      for (let side = 0; side < 2; side++) {
        const node = maps[side].fields.get(path);
        const first = lineAt(side, node.keyStart);
        const last = lineAt(side, Math.max(node.keyStart, node.end - 1));
        for (let i = first; i <= last; i++) lines[side][i].moved = true;
      }
    }
  }
  // Align source lines by semantic path rather than by screen row. Values may
  // differ, but a shared path still pairs; absent/moved paths get blank partners.
  const anchors = maps.map((map, side) => {
    const keys = new Array(lines[side].length);
    for (const [path, node] of map.fields) {
      const first = lineAt(side, node.keyStart);
      const last = lineAt(side, Math.max(node.keyStart, node.end - 1));
      keys[first] ??= JSON.stringify([path, 'start']);
      if (last !== first && ['mapping', 'sequence'].includes(node.kind) && /^[\s}\],]*$/.test(lines[side][last].text)) {
        keys[last] ??= JSON.stringify([path, 'end']);
      }
    }
    return lines[side].map((row, index) => keys[index] ?? `text:${row.text.trim().replace(/,$/, '')}`);
  });
  const aligned = [];
  let l = 0, r = 0;
  for (const part of diffArrays(anchors[0], anchors[1])) {
    for (const unused of part.value) {
      if (part.removed) aligned.push({ left: l++, right: null });
      else if (part.added) aligned.push({ left: null, right: r++ });
      else aligned.push({ left: l++, right: r++ });
    }
  }
  return { left: lines[0], right: lines[1], aligned };
}
